import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { MiscellaneousItem, Trailer, TruckDriver, Truckload } from '@paragon/contract-library';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch, naturalSortByProperty } from '@paragon/utilities';

import { addBundlesToTruckload, createTruckload, editTruckload } from '../../../actions/truckloadActions';
import { getActiveTruckloadsForCheckedBundlesWithSameProject } from '../../../selectors/scheduleGrid';
import { getTruckloadsForTruckloadGrid } from '../../../selectors/truckloadGrid';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { formatDateToString } from '../../pages/gridUtilities';
import { MiscellaneousItemsTable } from './MiscellaneousItemsTable';
import { TruckloadProperties } from './TruckloadProperties';

function getTruckloadDescriptor(
  truckload: Truckload,
  trailers: { [trailerGuid: string]: Trailer },
  truckDrivers: { [truckDriverGuid: string]: TruckDriver },
) {
  return `ID: ${truckload.id}${
    truckload.trailerGuid && trailers[truckload.trailerGuid] ? ` Trailer: ${trailers[truckload.trailerGuid].name}` : ''
  }${
    truckload.truckDriverGuid && truckDrivers[truckload.truckDriverGuid]
      ? ` Driver: ${truckDrivers[truckload.truckDriverGuid].name}`
      : ''
  }${truckload.dateShipped ? ` Ship Date: ${formatDateToString(truckload.dateShipped)}` : ''}`;
}

export interface CreateOrAddToTruckloadDialogProps {
  readonly bundleGuids: readonly string[];
}

export function CreateOrAddToTruckloadDialog(props: CreateOrAddToTruckloadDialogProps) {
  const { bundleGuids } = props;
  const dispatch = useAppDispatch();
  const activeTruckloadsForCheckedBundlesWithSameProject = useAppSelector(
    getActiveTruckloadsForCheckedBundlesWithSameProject,
  );
  const truckloadsForTruckloadGrid = useAppSelector(getTruckloadsForTruckloadGrid);
  const truckloads = useAppSelector((state) => state.truckloads);
  const trailers = useAppSelector((state) => state.trailers);
  const truckDrivers = useAppSelector((state) => state.truckDrivers);

  const [createOrAddTo, setCreateOrAddTo] = useState<'create' | 'add-to'>('create');

  // Create properties
  const [trailer, setTrailer] = useState<Trailer | null>(null);
  const [truckDriver, setTruckDriver] = useState<TruckDriver | null>(null);
  const [shipDate, setShipDate] = useState<Date | null>(null);
  const [miscellaneousItemsCreate, setMiscellaneousItemsCreate] = useState<readonly MiscellaneousItem[]>([]);

  // Add to properties
  const [filterToTruckloadsForSameProject, setFilterToTruckloadsForSameProject] = useState<boolean>(true);
  const [selectedTruckloadGuid, setSelectedTruckloadGuid] = useState<string | null>(null);
  const [miscellaneousItemsAddTo, setMiscellaneousItemsAddTo] = useState<readonly MiscellaneousItem[]>([]);

  const [submitDisabledDueToPendingMiscellaneousItems, setSubmitDisabledDueToPendingMiscellaneousItems] =
    useState<boolean>(false);

  function submit() {
    switch (createOrAddTo) {
      case 'create': {
        dispatch(
          createTruckload(
            bundleGuids,
            trailer?.guid ?? null,
            truckDriver?.guid ?? null,
            shipDate,
            miscellaneousItemsCreate,
          ),
        );
        break;
      }
      case 'add-to': {
        dispatch(addBundlesToTruckload(selectedTruckloadGuid!, bundleGuids));
        const miscellaneousItemsAreEdited =
          miscellaneousItemsAddTo !== truckloads[selectedTruckloadGuid!].miscellaneousItems;
        if (miscellaneousItemsAreEdited) {
          const { guid, trailerGuid, truckDriverGuid, dateShipped } = truckloads[selectedTruckloadGuid!];
          dispatch(
            editTruckload(
              guid,
              trailerGuid,
              truckDriverGuid,
              dateShipped ? new Date(dateShipped) : null,
              miscellaneousItemsAddTo,
            ),
          );
        }
        break;
      }
      default:
        assertExhaustiveSwitch(createOrAddTo);
    }
    dispatch(closeCurrentPopup());
  }

  let submitText: string;
  switch (createOrAddTo) {
    case 'create': {
      submitText = 'Create';
      break;
    }
    case 'add-to': {
      submitText = 'Add To';
      break;
    }
    default:
      assertExhaustiveSwitch(createOrAddTo);
  }

  let submitDisabled: boolean;
  switch (createOrAddTo) {
    case 'create': {
      submitDisabled = submitDisabledDueToPendingMiscellaneousItems;
      break;
    }
    case 'add-to': {
      submitDisabled = selectedTruckloadGuid === null || submitDisabledDueToPendingMiscellaneousItems;
      break;
    }
    default:
      assertExhaustiveSwitch(createOrAddTo);
  }

  return (
    <Dialog
      title="Create/Add to Truckload"
      open
      submitText={submitText}
      onSubmit={submit}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '900px', height: '700px' }}
      submitDisabled={submitDisabled}
      enterKeySubmits
      insistent
    >
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={createOrAddTo}
          exclusive
          onChange={(event, value) => {
            if (value !== null) setCreateOrAddTo(value);
          }}
        >
          <ToggleButton value="create">Create</ToggleButton>
          <ToggleButton value="add-to">Add To</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {createOrAddTo === 'create' && (
        <TruckloadProperties
          trailer={trailer}
          setTrailer={setTrailer}
          truckDriver={truckDriver}
          setTruckDriver={setTruckDriver}
          shipDate={shipDate}
          setShipDate={setShipDate}
          miscellaneousItems={miscellaneousItemsCreate}
          setMiscellaneousItems={setMiscellaneousItemsCreate}
          setParentSubmitDisabled={setSubmitDisabledDueToPendingMiscellaneousItems}
        />
      )}
      {createOrAddTo === 'add-to' && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterToTruckloadsForSameProject}
                onChange={(event) => {
                  setFilterToTruckloadsForSameProject(event.target.checked);
                  setSelectedTruckloadGuid(null);
                  setMiscellaneousItemsAddTo([]);
                }}
              />
            }
            label="Filter to truckloads for the same project"
          />
          <Autocomplete
            sx={{ mt: 1 }}
            size="small"
            options={naturalSortByProperty(
              filterToTruckloadsForSameProject
                ? activeTruckloadsForCheckedBundlesWithSameProject
                : truckloadsForTruckloadGrid,
              (truckload) => getTruckloadDescriptor(truckload, trailers, truckDrivers),
            )}
            getOptionLabel={(truckload) => getTruckloadDescriptor(truckload, trailers, truckDrivers)}
            renderOption={(props, truckload) => (
              <li {...props} key={truckload.guid}>
                {getTruckloadDescriptor(truckload, trailers, truckDrivers)}
              </li>
            )}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Select Truckload" />}
            value={
              selectedTruckloadGuid
                ? (activeTruckloadsForCheckedBundlesWithSameProject.find(
                    (truckload) => truckload.guid === selectedTruckloadGuid,
                  ) ?? null)
                : null
            }
            onChange={(event, truckload) => {
              setSelectedTruckloadGuid(truckload?.guid ?? null);
              setMiscellaneousItemsAddTo(truckload?.miscellaneousItems ?? []);
            }}
          />
          {selectedTruckloadGuid && (
            <MiscellaneousItemsTable
              miscellaneousItems={miscellaneousItemsAddTo}
              setMiscellaneousItems={setMiscellaneousItemsAddTo}
              setParentSubmitDisabled={setSubmitDisabledDueToPendingMiscellaneousItems}
            />
          )}
        </>
      )}
    </Dialog>
  );
}
